import { IdcardOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { Form, InputNumber, Radio, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import { cn } from "../../../../utils/cn.ts"
import { type IdConfiguration } from "../../redux/settings.types.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"

export type LeadProjectIdFormData = Pick<IdConfiguration, "mode" | "start_from">

const METHODS: {
  value: LeadProjectIdFormData["mode"]
  label: string
  description: string
}[] = [
  {
    value: "incremental",
    label: "Incremental",
    description: "Assign consecutive numbers (e.g. 000001, 000002, …)"
  },
  {
    value: "random",
    label: "Random",
    description: "Assign a random number within the defined range"
  }
]

interface LeadProjectIdCardProps {
  form: FormInstance<LeadProjectIdFormData>
  locked?: boolean
}

function SectionLabel({
  text,
  hint
}: {
  text: string
  hint: string
}) {
  return (
    <span className="flex flex-col">
      <Typography.Text className="font-semibold text-[15px]">
        {text}
      </Typography.Text>
      <Typography.Text type="secondary" className="font-normal">
        {hint}
      </Typography.Text>
    </span>
  )
}

export function LeadProjectIdCard({
  form,
  locked = false
}: LeadProjectIdCardProps) {
  const mode = Form.useWatch("mode", form)

  return (
    <SettingsCard
      iconWrapperClassName="bg-blue-100"
      icon={<IdcardOutlined className="text-blue-600" />}
      title="Lead & Project ID"
      subtitle="Configure how new leads and projects will be numbered."
    >
      <Form.Item
        name="mode"
        required
        label={
          <SectionLabel
            text="Numbering method"
            hint="Choose how the lead and project IDs will be generated."
          />
        }
        rules={[{ required: true }]}
      >
        <Radio.Group className="w-full">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {METHODS.map((method) => {
              const selected = mode === method.value

              return (
                <label
                  key={method.value}
                  className={cn(
                    "flex items-start gap-3 rounded-lg border p-4 transition-colors cursor-pointer",
                    selected
                      ? "border-primary bg-primary-bg"
                      : "border-[#E5E7EB] bg-white hover:border-gray-300"
                  )}
                >
                  <Radio
                    value={method.value}
                    aria-label={method.label}
                    className="mt-0.5"
                  />
                  <span className="flex flex-col">
                    <Typography.Text className="font-semibold text-[15px]">
                      {method.label}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {method.description}
                    </Typography.Text>
                  </span>
                </label>
              )
            })}
          </div>
        </Radio.Group>
      </Form.Item>

      {mode === "incremental" && (
        <Form.Item
          name="start_from"
          required
          label={
            <SectionLabel
              text="Start from"
              hint="Enter the starting number for new leads and projects."
            />
          }
          rules={[
            { required: true, message: "Choose a starting number." },
            {
              type: "integer",
              min: 1,
              max: 900000,
              message: "Must be between 000001 and 900000."
            }
          ]}
          extra={
            <span className="mt-1 flex items-center gap-1.5">
              <InfoCircleOutlined className="text-gray-400" />
              <Typography.Text type="secondary">
                {locked
                  ? "Locked — the starting number can be set only once."
                  : "Any number from 000001 to 900000. New leads and projects will be numbered from here."}
              </Typography.Text>
            </span>
          }
        >
          <InputNumber<number>
            min={1}
            max={900000}
            precision={0}
            controls={false}
            disabled={locked}
            aria-label="Start from"
            className="w-full max-w-[280px]"
            formatter={(value) =>
              value === undefined || value === null
                ? ""
                : String(value).padStart(6, "0")
            }
            parser={(value) => Number((value ?? "").replace(/\D/g, "")) || 1}
          />
        </Form.Item>
      )}
    </SettingsCard>
  )
}
